import { Dimensions, type OpaqueColorValue } from "react-native";
import type { ColorTokens } from "tamagui";
import { type GetThemeValueForKey, isWeb } from "@tamagui/web";
import { validToken } from "../validToken";
import { Loader } from "./Loader";
import { type ViewProps, YStack } from "./Stacks";
export type FullscreenSpinnerProps = ViewProps & {
  /**
   * For a spinner that is the whole page. A scrolling route gives `flex: 1`
   * only the loader's own height, which pins it to the top; this floors the box
   * at the viewport instead. Web-only: `dvh` is not a native unit, and native
   * screens come bounded.
   */
  viewportFloor?: boolean;
};
export const FullscreenSpinner = ({
  viewportFloor,
  ...props
}: FullscreenSpinnerProps) => {
  return <YStack flex={1} justifyContent="center" alignItems="center" role="progressbar" minHeight={viewportFloor && isWeb ? validToken("100dvh") : undefined} {...props} data-sentry-element="YStack" data-sentry-component="FullscreenSpinner" data-sentry-source-file="FullscreenSpinner.tsx">
      <Loader data-sentry-element="Loader" data-sentry-source-file="FullscreenSpinner.tsx" />
    </YStack>;
};

/**
 * Similar to fullscreenspinner, this should be used when we need to overlay the
 * entire screen with a spinner while a component/components render beneath
 * it. Commons use cases would be iFrames or scroll views with a lot of content
 *
 * We should possibly look into making this a more generic overlay component in
 * a provider that can be easily triggered from anywhere in the app and is
 * therefore not subject to h & w of parent components
 */
type FullscreenSpinnerOverlayProps = {
  heightOffset?: number;
  overlayColor?: ColorTokens | OpaqueColorValue | GetThemeValueForKey<"backgroundColor">;
  stackProps?: ViewProps;
};
export const FullscreenSpinnerOverlay = ({
  heightOffset,
  overlayColor,
  stackProps
}: FullscreenSpinnerOverlayProps) => {
  const windowWidth = Dimensions.get("window").width;
  const windowHeight = Dimensions.get("window").height;
  return <YStack flex={1} justifyContent="center" alignItems="center" height={isWeb ? "100%" : windowHeight} width={isWeb ? "100%" : windowWidth} backgroundColor={overlayColor} zIndex={1000} opacity={1} position="absolute" role="progressbar" {...stackProps} data-sentry-element="YStack" data-sentry-component="FullscreenSpinnerOverlay" data-sentry-source-file="FullscreenSpinner.tsx">
      <YStack position={heightOffset ? "relative" : undefined} y={heightOffset || undefined} data-sentry-element="YStack" data-sentry-source-file="FullscreenSpinner.tsx">
        <Loader data-sentry-element="Loader" data-sentry-source-file="FullscreenSpinner.tsx" />
      </YStack>
    </YStack>;
};